import * as React from "react";
import { Check, Command, DraftingCompass, LifeBuoy, Monitor, Send } from "lucide-react";
import { NavMain } from "@/components/nav-main";
import { NavSecondary } from "@/components/nav-secondary";
import { NavUser } from "@/components/nav-user";
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuItem,
} from "@/components/ui/sidebar";
import { t } from "i18next";
import { Routes } from "@/router/routes";
import { Tenant, useTenants } from "@/api/tenant.api";
import { useUserData } from "@/api/user.api";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocation, useNavigate } from "react-router";
import { useCallback, useMemo } from "react";
import { Resource, UserDataDto } from "@exibra/shared";

const data = {
    navMain: [
        {
            title: t("templates.title"),
            url: `${Routes.Templates}`,
            icon: DraftingCompass,
        },
        {
            title: t("screens.title"),
            url: `${Routes.Screens}`,
            icon: Monitor,
        },
    ],
    navSecondary: [
        {
            title: t("users.title"),
            url: Routes.Users,
            icon: LifeBuoy,
            enabled: (userData: UserDataDto, tenantId: number) =>
                userData.tenantPermissions[tenantId]?.some((p) => p.module === Resource.User),
        },
        {
            title: t("billing.title"),
            url: "#",
            icon: Send,
            enabled: (userData: UserDataDto, tenantId: number) =>
                userData.tenantPermissions[tenantId]?.some((p) => p.module === Resource.Billing),
        },
    ],
};

/**
 * The props for the AppSidebar component
 */
interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
    /** The tenant to display in the sidebar */
    tenant: Tenant;
}

/**
 * The main sidebar component for the application
 */
export function AppSidebar({ tenant, ...props }: AppSidebarProps) {
    const { data: userData } = useUserData();
    const { data: tenants } = useTenants();
    const hasManyTenants = tenants && tenants.length > 1;
    const location = useLocation();
    const navigate = useNavigate();

    const navItems = useMemo(() => {
        return data.navMain.map((item) => ({
            ...item,
            isActive: location.pathname.includes(item.url),
        }));
    }, [location.pathname]);

    const onTenantChange = useCallback(
        (tenantId: number) => {
            navigate(`/${Routes.Tenant}/${tenantId}`);
        },
        [navigate],
    );

    return (
        <Sidebar variant="inset" {...props}>
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem>
                        {hasManyTenants ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                                        <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                                            <Command className="size-4" />
                                        </div>
                                        <div className="grid flex-1 text-left text-sm leading-tight">
                                            <span className="truncate font-semibold">
                                                Exibra CMS
                                            </span>
                                            <span className="truncate text-xs">{tenant?.name}</span>
                                        </div>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="start"
                                    className="w-[--radix-dropdown-menu-trigger-width]"
                                >
                                    {tenants?.map((t: Tenant) => (
                                        <DropdownMenuItem
                                            key={t.id}
                                            onClick={() => onTenantChange?.(t.id)}
                                        >
                                            <span className="flex-1">{t.name}</span>
                                            {t.id === tenant?.id && <Check className="size-4" />}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="flex w-full items-center gap-3 rounded-lg px-3 py-2">
                                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                                    <Command className="size-4" />
                                </div>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-semibold">Exibra CMS</span>
                                    <span className="truncate text-xs">{tenant?.name}</span>
                                </div>
                            </div>
                        )}
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>
            <SidebarContent>
                <NavMain items={navItems} />
                {/* @ts-expect-error this is fine */}
                <NavSecondary items={data.navSecondary} className="mt-auto" />
            </SidebarContent>
            <SidebarFooter>{userData && <NavUser user={userData.data} />}</SidebarFooter>
        </Sidebar>
    );
}
